import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
// Constants - functions
import { pathHomePage, WEB_SERVICE_MIMS } from "../../config/constants";
// Components
import { TableHeader, HeaderSection, HeaderTitle, FilterButton, FilterText, FilterIndicatorImg } from "./styles";
import Axios from "../../config/axios";
import Loader from "../loader";
import PatientNavigation from "../patientNavigation";
import SoftwareMenu from "../softwareMenu";
import Title from "../title";
import MessageCanvas from "./sections/messageCanvas";
import NewMessage from "./sections/newMessage";
// Assets
import IcoArrowUp from "../../assets/icons/ico_arrow_up.svg";
import IcoArrowDown from "../../assets/icons/ico_arrow_down.svg";

const PatientMessages = () => {
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);
  const [rechargeMessages, setRechargeMessages] = useState(false);
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [patientProtocols, setPatientProtocols] = useState([]);
  // Filters
  const [isSortedByType, setIsFilteredByType] = useState(false);
  const [currentFilterType, setCurrentFilterType] = useState("");
  const [isSortedByDate, setIsFilteredByDate] = useState(true);
  const [isSortedByDateReversed, setIsFilteredByDateReversed] = useState(false);
  const [isSortedByStatus, setIsFilteredByStatus] = useState(false);
  const [currentFilterStatus, setCurrentFilterStatus] = useState("");

  const { clinicianToken, patientId, software } = useParams();
  const [t] = useTranslation("global");

  // Get coach messages
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/coach-messages/get-messages/${patientId}`)
      .then(({ data }) => {
        // Filtering messages by date
        const sortingByDate = data.sort((a, b) => {
          if (a.DELETE_TIME === null) return 1;
          if (b.DELETE_TIME === null) return -1;
          return new Date(a.DELETE_TIME) > new Date(b.DELETE_TIME) ? -1 : 1;
        });
        setMessages(sortingByDate);
        setFilteredMessages(sortingByDate);
        setIsLoadingMessages(false);
      })
      .catch((error) => {
        console.log("Endpoint to get coach messages: ", error);
      });
  }, [patientId, rechargeMessages]);

  // Get patient protocols
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/prescriptions/get-protocols/${patientId}/${software}`)
      .then(({ data }) => {
        const patientProtocols = [];
        data.forEach((protocol) => {
          patientProtocols.push(protocol.PROTOCOL_ID);
        });
        setPatientProtocols(patientProtocols);
      })
      .catch((error) => {
        console.log("Endpoint to get protocols id's from a patient: ", error);
      });
  }, [patientId, software]);

  const renderTitle = () => {
    if (messages.length) {
      return <Title title={t("coachMessages_messagesList")} marginBottom="10px" />;
    }
  };

  const renderFilters = () => {
    if (messages.length) {
      const clearFilters = (id) => {
        if (id !== "type") {
          setIsFilteredByType(false);
          setCurrentFilterType("");
        }
        if (id !== "date") {
          setIsFilteredByDate(false);
        }
        if (id !== "status") {
          setIsFilteredByStatus(false);
          setCurrentFilterStatus("");
        }
      };

      // const sortByType = () => {
      //   setIsFilteredByType(true);
      //   // Removing other filters
      //   clearFilters("type");
      //   // Filtering messages by type: Notifications
      //   if (!currentFilterType || currentFilterType === "notification") {
      //     setCurrentFilterType("welcome");
      //     const sortingByNotification = messages.sort((a) => {
      //       return a.TYPE === "NOTIFICATION" ? -1 : 1;
      //     });
      //     return setFilteredMessages(sortingByNotification);
      //   }
      //   // Filtering messages by type: Welcome
      //   if (currentFilterType === "welcome") {
      //     setCurrentFilterType("activity");
      //     const sortingByGreeting = messages.sort((a) => {
      //       return a.TYPE === "GREETING" ? -1 : 1;
      //     });
      //     return setFilteredMessages(sortingByGreeting);
      //   }
      //   // Filtering messages by type: Activity start
      //   if (currentFilterType === "activity") {
      //     setCurrentFilterType("notification");
      //     const sortingByActivity = messages.sort((a) => {
      //       return a.TYPE === "ON_PROTOCOL" ? -1 : 1;
      //     });
      //     return setFilteredMessages(sortingByActivity);
      //   }
      // };

      const sortByDate = () => {
        setIsFilteredByDate(true);
        // Removing other filters
        clearFilters("date");
        // Reversing the stored array of messages
        if (isSortedByDate) {
          setIsFilteredByDateReversed(!isSortedByDateReversed);
          return setFilteredMessages([...filteredMessages].reverse());
        } else {
          setIsFilteredByDateReversed(false);
        }
        // Filtering messages by date
        const sortingByDate = messages.sort((a, b) => {
          if (a.DELETE_TIME === null) return 1;
          if (b.DELETE_TIME === null) return -1;
          return new Date(a.DELETE_TIME) > new Date(b.DELETE_TIME) ? -1 : 1;
        });
        return setFilteredMessages(sortingByDate);
      };

      const sortByStatus = () => {
        setIsFilteredByStatus(true);
        // Removing other filters
        clearFilters("status");
        // Filtering messages by status: Active
        if (!currentFilterStatus || currentFilterStatus === "active") {
          // Setting next filter
          setCurrentFilterStatus("pending");
          const sortingActiveStatus = messages.sort((a) => {
            return !a.DELETE_TIME || (!a.SHOW_ONLY_ONCE && a.DELETE_TIME && new Date(a.DELETE_TIME) > new Date()) ? -1 : 1;
          });
          return setFilteredMessages(sortingActiveStatus);
        }
        // Filtering messages by status: Pending
        if (currentFilterStatus === "pending") {
          // Setting next filter
          setCurrentFilterStatus("sent");
          const sortingPendingStatus = messages.sort((a) => {
            return a.SHOW_ONLY_ONCE && a.DELETE_TIME && new Date(a.DELETE_TIME) > new Date() ? -1 : 1;
          });
          return setFilteredMessages(sortingPendingStatus);
        }
        // Filtering messages by status: Sent
        if (currentFilterStatus === "sent") {
          // Setting next filter
          setCurrentFilterStatus("active");
          const sortingSentStatus = messages.sort((a) => {
            return a.DELETE_TIME && new Date(a.DELETE_TIME) < new Date() ? -1 : 1;
          });
          return setFilteredMessages(sortingSentStatus);
        }
      };

      const renderCurrentTypeFilter = () => {
        const filterTitle = {
          welcome: t("coachMessages_notification").toLowerCase(),
          activity: t("coachMessages_greeting").toLowerCase(),
          notification: t("coachMessages_onProtocol").toLowerCase(),
        };
        if (isSortedByType) {
          return <FilterText>{`${t("coachMessages_filters_type")}: ${filterTitle[currentFilterType]}`}</FilterText>;
        }
        return <HeaderTitle>{`${t("coachMessages_filters_type")}`}</HeaderTitle>;
      };

      const renderCurrentStatusFilter = () => {
        const filterTitle = {
          pending: t("coachMessages_status_active").toLowerCase(),
          sent: t("coachMessages_status_pending").toLowerCase(),
          active: t("coachMessages_status_sent").toLowerCase(),
        };
        if (isSortedByStatus) {
          return <FilterText>{`${t("coachMessages_filters_status")}: ${filterTitle[currentFilterStatus]}`}</FilterText>;
        }
        return <FilterText>{t("coachMessages_filters_status")}</FilterText>;
      };

      return (
        <TableHeader>
          <HeaderSection paddingLeft="20px" width="235px">
            {/* <FilterButton onClick={sortByType}> */}
            {renderCurrentTypeFilter()}
            {/* {isSortedByType && <FilterIndicatorImg src={IcoArrowDown} alt="ico-arrow" />} */}
            {/* </FilterButton> */}
          </HeaderSection>
          <HeaderSection paddingLeft="18px" width="125px">
            <FilterButton onClick={sortByDate}>
              <FilterText>{t("date").toUpperCase()}</FilterText>
              {isSortedByDate && <FilterIndicatorImg src={isSortedByDateReversed ? IcoArrowUp : IcoArrowDown} alt="ico-arrow" />}
            </FilterButton>
          </HeaderSection>
          <HeaderSection paddingLeft="18px" width="200px">
            <FilterButton onClick={sortByStatus}>
              {renderCurrentStatusFilter()}
              {isSortedByStatus && <FilterIndicatorImg src={IcoArrowDown} alt="ico-arrow" />}
            </FilterButton>
          </HeaderSection>
        </TableHeader>
      );
    }
  };

  const renderMessages = () => {
    return filteredMessages.map((message) => {
      const messageTitles = {
        NOTIFICATION: t("coachMessages_notification"),
        GREETING: t("coachMessages_greeting"),
        ON_PROTOCOL: t("coachMessages_onProtocol"),
      };
      return (
        <MessageCanvas
          key={message.COACH_MESSAGES_ID}
          title={messageTitles[message.TYPE]}
          isOnlyDisplayed={false}
          patientProtocols={patientProtocols}
          COACH_MESSAGES_ID={message.COACH_MESSAGES_ID}
          COACH_MESSAGES_PER_PATIENT_ID={message.COACH_MESSAGES_PER_PATIENT_ID}
          COACH_MESSAGES_PER_PROTOCOL_ID={message.COACH_MESSAGES_PER_PROTOCOL_ID}
          messageType={message.TYPE}
          protocolId={message.PROTOCOL_ID ? message.PROTOCOL_ID : 0}
          messageText={message.MESSAGE}
          showOnlyOnce={message.SHOW_ONLY_ONCE}
          launchDays={message.LAUNCH_DAYS}
          launchTime={message.LAUNCH_TIME ? message.LAUNCH_TIME.slice(0, 5) : ""}
          deleteTime={message.DELETE_TIME}
          setIsLoadingMessages={setIsLoadingMessages}
          rechargeMessages={rechargeMessages}
          setRechargeMessages={setRechargeMessages}
        />
      );
    });
  };

  return (
    <>
      {isLoadingMessages ? (
        <Loader />
      ) : (
        <>
          <PatientNavigation patientId={patientId} section={software} />
          <SoftwareMenu
            prescriptionsPath={`${pathHomePage}/prescriptions/${software}/${clinicianToken}/${patientId}`}
            diagnosisPrescriptionsPath={`${pathHomePage}/diagnosis-prescriptions/${clinicianToken}/${patientId}/${software}`}
            questionnairePath={`${pathHomePage}/emotional-questions/${clinicianToken}/${patientId}/${software}`}
            messagesPath={`${pathHomePage}/messages/${clinicianToken}/${patientId}/${software}`}
          />
          <NewMessage
            messages={messages}
            patientProtocols={patientProtocols}
            setIsLoadingMessages={setIsLoadingMessages}
            rechargeMessages={rechargeMessages}
            setRechargeMessages={setRechargeMessages}
          />
          {renderTitle()}
          {renderFilters()}
          {renderMessages()}
        </>
      )}
    </>
  );
};

export default PatientMessages;
